'use client';

import { useContext, useEffect, useState, type FC } from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Heading,
  Loading,
  PanelCenter,
  PanelHeader,
  PanelRight,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { downloadDocument } from '@/_api/client-documents';
import { loadThumbnails } from '@/_utils/pdfs';
import { convertToRanges } from '@/_utils/documents';
import { splitDocuments } from '@/_api/admin-documents';
import { PdfPage } from './components/PdfPage';
import styles from './ReviewDialog.module.css';
import { ReviewDialogProps } from './ReviewDialog.types';
import { BulkDocument } from '@/_types/models/documents';
import { getClasses } from '@/_utils/classnames';
import { DocumentsPanel } from './components/DocumentsPanel/DocumentsPanel';
import { useBulkUploadProcessor } from '@/_contexts/admin/BulkUploadProcessorProvider';

export const ReviewDialog: FC<ReviewDialogProps> = ({
  childDocuments,
  isOpen,
  onClose,
  uploadedFile,
  questionnaire,
  reloadQuestionnairePreview,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [currentlySelectedDoc, setCurrentlySelectedDoc] =
    useState<BulkDocument | null>(null);
  const [documents, setDocuments] = useState<Array<BulkDocument>>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(2); // columns: 1-4
  const [showDeletedPages, setShowDeletedPages] = useState<boolean>(false);
  const [showPageChips, setShowPageChips] = useState<boolean>(false);
  const [editingDocId, setEditingDocId] = useState<number | null>(null);
  const { activePageFilter } = useBulkUploadProcessor();

  useEffect(() => {
    setDocuments(
      childDocuments
        .filter((doc) => doc.name !== '__to_remove__')
        .map((doc) => {
          const pageRanges = convertToRanges(doc.parent_pages || []).join(', ');
          return {
            id: doc.id,
            name: doc.name,
            differentiator: doc.differentiator,
            pageRanges,
            pages: doc.parent_pages,
          };
        }),
    );
  }, [childDocuments]);

  useEffect(() => {
    if (
      !uploadedFile.source ||
      !questionnaire?.company_id ||
      !token ||
      !addToast
    ) {
      return;
    }

    const loadDocument = async () => {
      try {
        setIsLoading(true);

        const response = await downloadDocument({
          companyId: questionnaire?.company_id,
          link: uploadedFile.source!,
          token,
        });

        const pdfPages = await loadThumbnails(response.document.data.link);

        setPages(pdfPages);
      } catch (error) {
        addToast('error', 'An error occurred while loading the document.', {
          error,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [addToast, questionnaire?.company_id, token, uploadedFile?.source]);

  // Check if there are documents without pages
  const documentsWithoutPages = documents.filter(
    (doc) => !doc.pages || doc.pages.length === 0,
  );
  const hasDocumentsWithoutPages = documentsWithoutPages.length > 0;

  const onDone = async () => {
    try {
      setIsLoading(true);

      await splitDocuments({
        companyId: questionnaire?.company_id!,
        extractedDocumentId: uploadedFile.id!,
        documents,
        token,
      });

      await reloadQuestionnairePreview();

      addToast('success', 'Saved');
      onClose();
    } catch (error) {
      addToast('error', 'An error occurred while splitting the document.', {
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const mainFlexClasses: string = getClasses(styles.mainFlex, {
    [styles.docSelected]: !!currentlySelectedDoc,
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      isFullScreen={true}
      disablePadding={true}
      isPersistent={true}
    >
      <Dialog.Content disablePadding={true}>
        <div className={styles.topHeaderWrap}>
          <PanelHeader>
            <Heading level={4}>
              Bulk Upload Processor Review: {uploadedFile.name}
              {uploadedFile.page_count
                ? ` (${uploadedFile.page_count} pages)`
                : ''}
            </Heading>

            <Flex isYCentered gap={8}>
              <Button
                intent="secondary"
                size="small"
                iconLeft="lightbulb"
                value="See what's new"
                onClick={() =>
                  window.open(
                    'https://helpcenter.soraban.com/bulk-upload-processor-new-ux',
                    '_blank',
                  )
                }
              />
              <ButtonGroup isSameLine>
                <Button
                  intent="ghost"
                  size="tiny"
                  isIconOnly={true}
                  iconLeft="minus"
                  value="Zoom out"
                  withTooltip
                  tooltipPlacement="bottom"
                  onClick={() =>
                    setZoomLevel((z) => Math.min(4, Math.max(1, z + 1)))
                  }
                />
                <Button
                  intent="ghost"
                  size="tiny"
                  isIconOnly={true}
                  iconLeft="plus"
                  value="Zoom in"
                  withTooltip
                  tooltipPlacement="bottom"
                  onClick={() =>
                    setZoomLevel((z) => Math.min(4, Math.max(1, z - 1)))
                  }
                />
                <Button
                  intent="danger-secondary"
                  isDisabled={isLoading}
                  onClick={onClose}
                  value="Close"
                />
                <Button
                  intent="primary"
                  isDisabled={isLoading || hasDocumentsWithoutPages}
                  onClick={onDone}
                  value="Finish Reviewing"
                  withTooltip={hasDocumentsWithoutPages}
                  tooltip={`Cannot finish: ${documentsWithoutPages.length} document${documentsWithoutPages.length === 1 ? '' : 's'} without pages`}
                />
              </ButtonGroup>
            </Flex>
          </PanelHeader>
        </div>
        <Flex>
          <PanelCenter className={styles.panelCenter} hasRight>
            <Flex className={mainFlexClasses} isWrapping gap={16}>
              {isLoading ? (
                <div className={styles.loadingWrap}>
                  <Loading size={56} />
                </div>
              ) : (
                <>
                  <div
                    className={`${styles.pageGrid} ${
                      zoomLevel <= 1
                        ? styles.columns1
                        : zoomLevel === 2
                          ? styles.columns2
                          : zoomLevel === 3
                            ? styles.columns3
                            : styles.columns4
                    }`}
                  >
                    {pages
                      .filter(({ pageNumber }: any) => {
                        // Deleted visibility
                        const marked = (documents || []).some(
                          (d: any) =>
                            d.name === '__to_remove__' &&
                            (d.pages || []).includes(pageNumber),
                        );
                        if (!showDeletedPages && marked) return false;

                        // Document/Unclassified filter
                        if (!activePageFilter) return true;
                        if (activePageFilter === '__unclassified__') {
                          const assigned = new Set<number>();
                          (documents || []).forEach((d: any) =>
                            (d.pages || []).forEach((p: number) =>
                              assigned.add(p),
                            ),
                          );
                          return !assigned.has(pageNumber);
                        }
                        const target = (documents || []).find(
                          (d: any) => d.id === activePageFilter,
                        );
                        const arr = Array.isArray(target?.pages)
                          ? (target?.pages as number[])
                          : [];
                        return arr.includes(pageNumber);
                      })
                      .map(({ pageNumber, thumbnail }, key) => (
                        <div key={key} className={styles.pageItem}>
                          <PdfPage
                            currentlySelectedDoc={currentlySelectedDoc}
                            documents={documents}
                            pageNumber={pageNumber}
                            setCurrentlySelectedDoc={setCurrentlySelectedDoc}
                            setDocuments={setDocuments}
                            thumbnail={thumbnail}
                            showDeletedPages={showDeletedPages}
                            editingDocId={editingDocId}
                          />
                        </div>
                      ))}
                  </div>
                </>
              )}
            </Flex>
          </PanelCenter>
          <PanelRight className={styles.panelRight}>
            <DocumentsPanel
              documents={documents}
              setDocuments={setDocuments}
              currentlySelectedDoc={currentlySelectedDoc}
              setCurrentlySelectedDoc={(d: BulkDocument | null) =>
                setCurrentlySelectedDoc(d)
              }
              isLoading={isLoading}
              isDragging={isDragging}
              setIsDragging={(d: boolean) => setIsDragging(d)}
              pages={pages}
              showPageChips={showPageChips}
              setShowPageChips={setShowPageChips}
              showDeletedPages={showDeletedPages}
              setShowDeletedPages={setShowDeletedPages}
              editingDocId={editingDocId}
              setEditingDocId={setEditingDocId}
              questionnaire={questionnaire}
            />
          </PanelRight>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
